import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of, firstValueFrom } from 'rxjs';
import { catchError, map, tap, finalize, take } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { EventService } from '../services/event.service';
import { TransferState, makeStateKey } from '@angular/platform-browser';

// Define una clave única para guardar eventos en TransferState
const EVENT_KEY = makeStateKey<any>('event-data');

@Injectable({ providedIn: 'root' })
export class EventResolver implements Resolve<any> {
  constructor(
    private eventService: EventService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: any,
    private transferState: TransferState
  ) {
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    const slug = route.paramMap.get('event_slug');
    const isServer = isPlatformServer(this.platformId);

    if (!slug) {
      return of(null);
    }

    // Crear una clave única para este evento basada en el slug
    const eventKey = makeStateKey<any>(`event-${slug}`);

    // Verificar si el evento ya está en el TransferState
    // Esto permite evitar peticiones duplicadas durante la hidratación de cliente
    if (!isServer && this.transferState.hasKey(eventKey)) {
      const cachedEvent = this.transferState.get(eventKey, null);
      // Limpiar después de usar
      this.transferState.remove(eventKey);
      return of(cachedEvent);
    }


    // Forzar sincronización en el lado del servidor para asegurar que los meta tags se establezcan correctamente
    if (isServer) {
      return this.getSyncServerEvent(slug, eventKey);
    }

    // Para el cliente, usar el flujo normal asíncrono
    return this.eventService.getEvents(slug).pipe(
      take(1),
      tap((events) => {
        if (events?.[0]) {
        }
      }),
      map((events) => {
        const event = events?.[0];

        if (!event) {
          return null;
        }

        return event;
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  // Este método realiza una petición síncrona en el servidor para asegurar que los datos
  // estén disponibles antes de que se complete la renderización
  private getSyncServerEvent(slug: string, eventKey: any): Observable<any> {
    return new Observable<any>((observer) => {
      console.log(`Ejecutando petición síncrona para evento: ${slug}`);

      // Usamos firstValueFrom para convertir el observable a promise
      firstValueFrom(this.eventService.getEvents(slug))
        .then((events) => {
          const event = events?.[0] || null;

          if (event) {
            console.log(`[SSR] Evento encontrado: ${event.name} (${event.id})`);

            // Guardar el evento en TransferState para que el cliente lo use
            this.transferState.set(eventKey, event);

            // Emitir el evento y completar
            observer.next(event);
            observer.complete();
          } else {
            console.warn(`[SSR] No se encontró evento con slug: ${slug}`);
            observer.next(null);
            observer.complete();
          }
        })
        .catch((error) => {
          console.error('[SSR] Error al obtener evento:', error);
          observer.error(error);
        });
    });
  }
}
